import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Lock, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import PasswordInput from '../components/PasswordInput';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

const ResetPasswordPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token = params.get('token') || '';
  const emailParam = params.get('email') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    if (!token) {
      setError('This reset link is missing its token. Please request a new one.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
      toast.success('Password updated');
      setTimeout(() => navigate('/login', { replace: true }), 1400);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO title="Reset password" description="Set a new password for your PDF Studio Pro account" />
      <div className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md" data-testid="reset-page">
          <div className="bg-card border border-border rounded-2xl shadow-xl p-6 sm:p-8">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center mb-3">
                <Lock className="w-6 h-6 text-primary-foreground" />
              </div>
              <h1 className="text-2xl font-bold font-heading tracking-tight">Choose a new password</h1>
              {emailParam && (
                <p className="text-sm text-muted-foreground mt-1">for <span className="font-medium">{emailParam}</span></p>
              )}
            </div>

            {done ? (
              <div className="flex flex-col items-center text-center py-4" data-testid="reset-success">
                <CheckCircle2 className="w-10 h-10 text-green-500 mb-2" />
                <p className="text-sm text-muted-foreground">Password updated. Redirecting to sign in&hellip;</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3" data-testid="reset-form">
                <PasswordInput
                  required
                  minLength={6}
                  placeholder="New password (min 6 chars)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  data-testid="input-new-password"
                />
                <PasswordInput
                  required
                  minLength={6}
                  placeholder="Confirm new password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  data-testid="input-confirm-password"
                />
                {error && <p className="text-sm text-destructive text-center" data-testid="reset-error">{error}</p>}
                <Button type="submit" disabled={submitting} className="w-full h-11 rounded-lg" data-testid="submit-reset-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Update password
                </Button>
              </form>
            )}
          </div>
          <p className="mt-4 text-center text-xs text-muted-foreground">
            <Link to="/login" className="hover:underline">Back to sign in</Link>
          </p>
        </div>
      </div>
    </>
  );
};

export default ResetPasswordPage;
